export const checkout = {
  url: "https://senior-activity-hub.impultienda.ar/checkout",
  price: "29",
  currency: "USD",
};

export const productName = "The Activity Director’s Year-Round Kit";

export const supportEmail = "support@stockdigital.shop";

export const images = {
  bookCover: "https://cdn.magicpatterns.com/uploads/sjkn7sJP3r8ykDrRcAojZe/01_portada.png",
  boxMockup: "https://cdn.magicpatterns.com/uploads/hT2sp5tepMFJLTSQY9Zz9r/Kit_premium_de_actividades_para_adultos_mayores.png",
};

export const hero = {
  eyebrow: "For senior living activity directors",
  promise: "Open the calendar, print the materials, and follow the instructions.",
  explanation:
    "10 ready-to-lead group activities with printable materials, coordinator instructions and 5 practical bonuses — so you can prepare your next session without starting from scratch.",
  trust: "Digital PDFs in English · One-time payment · 10-day money-back guarantee",
};

/* Real interior pages — an activity first, the cover last. */
export const samplePages = [
  { label: "G001 · Everyday Object Bingo", src: "https://cdn.magicpatterns.com/uploads/7Sc3k8ocSKSwyBHd22WuCJ/02_bingo_objetos.png" },
  { label: "G002 · This or That? cards", src: "https://cdn.magicpatterns.com/uploads/4QpsiDyVz8b4bfcemeSZDN/03_this_or_that.png" },
  { label: "G008 · Shared Garden layout", src: "https://cdn.magicpatterns.com/uploads/wvCNv47S8RBmevEjJbNm3T/04_garden_plan.png" },
  { label: "G009 · Find the Connection cards", src: "https://cdn.magicpatterns.com/uploads/uvg262winidh6h11vHabHe/05_find_the_connection.png" },
  { label: "Volume 01 · Cover", src: images.bookCover },
];

export const problems = [
  {
    icon: "clock",
    title: "Planning after your shift",
    description: "You finish today’s program, then spend the evening searching for tomorrow’s.",
  },
  {
    icon: "bookmark",
    title: "Ideas without instructions",
    description: "A saved pin gives you the idea, not the rules, questions or materials to run it.",
  },
  {
    icon: "users",
    title: "One room, different abilities",
    description: "Some residents want a challenge. Others need a seated or simpler way to join in.",
  },
  {
    icon: "refresh",
    title: "Plans change at short notice",
    description: "A smaller group or a cancelled outing, and you need something ready to lead.",
  },
] as const;

export const steps = [
  {
    icon: "calendar",
    title: "Choose your activity",
    description: "Use the calendar and activity guide to select a session for your group.",
  },
  {
    icon: "printer",
    title: "Print what you need",
    description: "Find the participant sheets, cards and materials for that activity.",
  },
  {
    icon: "clipboard",
    title: "Follow the steps",
    description: "Use the coordinator instructions and adapt participation to your residents.",
  },
] as const;

export const book = {
  label: "Volume 01",
  title: "Group Games & Social Fun",
  description:
    "A standalone activity book with 10 complete group sessions — themed bingo, choice activities, cooperative games and seated sessions with clear rules.",
  components: [
    { icon: "users", label: "Ready-to-lead group activities" },
    { icon: "clipboard", label: "Coordinator run sheets" },
    { icon: "type", label: "Large-print participant materials" },
    { icon: "message", label: "Opening scripts and step-by-step instructions" },
    { icon: "sliders", label: "Practical participation adaptations" },
    { icon: "calendar", label: "12-month activity calendar" },
  ],
  sampleActivities: [
    { code: "G001", name: "Everyday Object Bingo" },
    { code: "G002", name: "This or That? Conversation Circle" },
    { code: "G008", name: "Let’s Plan a Shared Garden" },
    { code: "G009", name: "Find the Connection" },
  ],
} as const;

export const bonuses = [
  {
    title: "The Last-Minute Activity Rescue Kit",
    benefit:
      "Keep 20 short activity options on hand for schedule changes, smaller groups and moments when you need a backup plan.",
    src: "https://cdn.magicpatterns.com/uploads/uH6wM8J8amxdGb3DejTPE7/mockup-the-last-minute-activity-rescue-kit.png",
  },
  {
    title: "The Resident Interests & Preferences Kit",
    benefit: "Use ready-made forms to learn what residents enjoy and choose activities with their preferences in mind.",
    src: "https://cdn.magicpatterns.com/uploads/e7KYTxLruYHJ6N6pdQ4uF9/mockup-the-resident-interests-preferences-kit.png",
  },
  {
    title: "The Monthly Materials Planner",
    benefit:
      "Organize supplies ahead of time with practical planning sheets, so you know what to prepare before the session.",
    src: "https://cdn.magicpatterns.com/uploads/kTBV6jPHRQtxfGB8eXTFmF/mockup-the-monthly-materials-planner.png",
  },
  {
    title: "The Printable Activity Invitation Pack",
    benefit:
      "Use printable invitation templates to announce upcoming activities without designing every notice from scratch.",
    src: "https://cdn.magicpatterns.com/uploads/kHG92FLtyQ7jouU1JLVVF6/mockup-the-printable-activity-invitation-pack.png",
  },
  {
    title: "The Team Handover & Volunteer Quick-Start Kit",
    benefit:
      "Give colleagues and volunteers clear handover notes and quick-start guidance for the activities they will help lead.",
    src: "https://cdn.magicpatterns.com/uploads/8MefcRWeC98Qbqb9HkARGm/mockup-the-team-handover-volunteer-quick-start-kit.png",
  },
];

export const offerIncluded = [
  "10 ready-to-lead group activities",
  "Coordinator instructions and opening scripts",
  "Large-print participant materials",
  "Practical activity adaptations",
  "12-month activity calendar",
  "All 5 bonus resources",
];

export const guarantee = {
  days: 10,
  text: `Open the materials and see how they fit your activity planning. If the kit isn’t right for you, contact ${supportEmail} within 10 days of purchase to request a refund.`,
};

export const faqs = [
  {
    question: "What exactly do I receive?",
    answer:
      "Volume 01 — Group Games & Social Fun with 10 ready-to-lead group activities, a 12-month activity calendar, large-print participant materials, coordinator instructions and 5 bonus resources, all as downloadable PDFs.",
  },
  {
    question: "How do I receive the files?",
    answer: "Your download details will be sent to the email address you provide at checkout.",
  },
  {
    question: "Are the materials in English?",
    answer: "Yes. Everything is a digital PDF in English. Nothing is shipped physically.",
  },
  {
    question: "Do I need to print the entire kit?",
    answer: "No. Print only the participant materials for the activity you choose.",
  },
  {
    question: "Will I need extra materials?",
    answer: "Some activities use everyday supplies. Each activity lists what to gather before you start.",
  },
  {
    question: "Can I adapt activities for different participants?",
    answer:
      "Yes. The kit includes practical participation adaptations, so you can adjust each activity to residents’ preferences and needs.",
  },
  {
    question: "Does the calendar replace our existing daily program?",
    answer:
      "No. The calendar helps you organize these activities alongside your existing program. The activities complement your current programming.",
  },
  {
    question: "Is this a one-time payment?",
    answer: `Yes. $${checkout.price} ${checkout.currency}, paid once. There is no subscription.`,
  },
  {
    question: "How does the 10-day guarantee work?",
    answer: `If the kit isn’t right for you, email ${supportEmail} within 10 days of purchase to request a refund.`,
  },
];
